import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import ordersCreateRouter from '@/apps/order/routes/ordersCreate';
import { SingleSettingsInterface } from '@/apps/order/interfaces/ordersCreate/DataPreparedConsumptionPointInterface';
import { useFeatureStore, useRouterStore } from '@/apps/order/stores';
import FormErrors from '@/utils/FormErrors';
import { autoUpdateSessionStorage, reactivityFix } from '@/apps/order/helper';
import {
  useOrdersCreateCalculatorStore,
  useOrdersCreateDataPreparedStore,
} from '@/apps/order/stores/ordersCreate/index';

import FormDataCartItemDeclarationOfIntentInterface from '@/apps/order/interfaces/ordersCreate/FormDataCartItemDeclarationOfIntentInterface';
import FormDataDeclarationOfIntent from '@/apps/order/interfaces/ordersCreate/FormDataDeclarationOfIntentInterface';
import FormDataConsumptionPointInterface from '@/apps/order/interfaces/ordersCreate/FormDataConsumptionPointInterface';
import OrdersCreateFormDataInterface from '@/apps/order/interfaces/OrdersCreateFormDataInterface';
import FiberConfigFormInterface from '@/apps/order/interfaces/fiber/FiberConfigFormInterface';
import ProductOptionInterface from '@/apps/order/interfaces/fiber/ProductOptionInterface';
import { mapOptionsToPayload } from '@/apps/order/helper/fiber';
import { hasMarginalPrice } from '@/apps/order/helper/marginalPrice';

const useOrdersCreateFormDataStore = defineStore('ordersCreateFormData', () => {
  const routerStore = useRouterStore();
  const { meta } = storeToRefs(routerStore);

  const dataPreparedStore = useOrdersCreateDataPreparedStore();
  const {
    getDataConsumptionPointByCategoryKey,
  } = dataPreparedStore as unknown as {
    getDataConsumptionPointByCategoryKey: (key: string) => SingleSettingsInterface | undefined;
  };
  const calculatorStore = useOrdersCreateCalculatorStore();
  const { formData: calculatorData, brandsSettings, transitionTariffs } = storeToRefs(calculatorStore);

  const formData = ref<OrdersCreateFormDataInterface>({
    customer: {
      exists: false,
      customerNumber: '',
      meterNumber: '',
    },
    categories: [],
    calculator: {
      usage: {},
      usageHt: {},
      usageNt: {},
      subcategories: {},
      measurementType: {},
      supplier: {},
      current_working_price: {},
      current_base_price: {},
      supplier_tariff: {},
      counterType: {},
    },
    address: {
      birthday: undefined,
      sharedLastName: undefined,
      lastName: undefined,
      // IOSUITE-7461 — seeded by the address step only where the question is actually asked
      // (the io.center relocation flow); it must stay unset in every other order process.
      newDeliveryAddress: undefined,
      billingAddressDifferent: false,
      billingAddress: {
        pobox: undefined,
        postcode: undefined,
        city: undefined,
        street: undefined,
        houseNumber: undefined,
        houseNumberAddon: undefined,
        firstName: undefined,
        lastName: undefined,
      },
    },
    payment: {
      sepa: false,
      yearly: false,
    },
    customFields: {},
    declarationOfIntent: {},
    cartItems: {},
    referralMarketing: {
      referred: false,
      customFields: undefined,
    },
    canEditAddress: false,
    previousAddress: {},
    externalOrderId: undefined,
  });
  reactivityFix(formData);
  autoUpdateSessionStorage(`${meta.value.ctx}.formData`, () => formData.value);
  const alreadyFetched = ref(false);
  const selectedTariffs = ref<Record<string, any>>({});
  reactivityFix(selectedTariffs);
  autoUpdateSessionStorage(`${meta.value.ctx}.selectedTariffs`, () => selectedTariffs.value, 1000);
  const continueAsLead = ref(false);
  autoUpdateSessionStorage(`${meta.value.ctx}.canContinueAsLead`, () => continueAsLead.value, 500);
  const formErrors = ref<FormErrors>(new FormErrors());

  const featureStore = useFeatureStore();
  const { referralMarketingIsActive } = storeToRefs(featureStore);

  const getFormAddressPostParams = (categoryKey: string): Record<string, any> => {
    const phoneNumbers: Record<string, string> = {};
    (formData.value.address.phone || []).forEach((number) => {
      if (number?.number && number.type) {
        phoneNumbers[number.type] = number.number;
      }
    });
    const selectedTariff = selectedTariffs.value[categoryKey];
    const hasCounterType = ['power', 'heatingPower'].includes(categoryKey);
    const counterType = calculatorData.value.counterType[categoryKey];
    const { currentRoute } = ordersCreateRouter;
    const formDataUuid = formData.value.uuid as unknown as string | undefined;
    const isEdit = !!formDataUuid
      && currentRoute.params.orderUuid === formDataUuid;

    let isDouble;
    if (isEdit) {
      const parsedUsage = parseInt(formData.value.rawData?.order_data.usage, 10);
      const parsedUsageHt = parseInt(formData.value.rawData?.order_data.usageHt, 10);
      const parsedUsageNt = parseInt(formData.value.rawData?.order_data.usageNt, 10);

      isDouble = Number.isNaN(parsedUsage) && (
        !Number.isNaN(parsedUsageHt) || !Number.isNaN(parsedUsageNt)
      );
    } else {
      isDouble = hasCounterType && counterType === 'double';
    }

    const usage = !isEdit
      ? formData.value.calculator.usage[categoryKey]
      : formData.value.rawData?.order_data.usage;
    const usageHt = !isEdit
      ? formData.value.calculator.usageHt[categoryKey]
      : formData.value.rawData?.order_data.usageHt;
    const usageNt = !isEdit
      ? formData.value.calculator.usageNt[categoryKey]
      : formData.value.rawData?.order_data.usageNt;

    const street = formData.value.calculator.street || calculatorData.value.street;
    const number = formData.value.calculator.number || calculatorData.value.number;

    return {
      type: categoryKey,
      bonuses_data: selectedTariff?.bonuses_data || [],
      brand_id: formData.value.calculator.brand_id,
      sales_channel_id: formData.value.calculator.sales_channel_id,
      ...(formData.value.calculator.salesPartnerNumber !== undefined ? {
        salesPartnerNumber: formData.value.calculator.salesPartnerNumber,
      } : {}),
      product_id: selectedTariffs.value[categoryKey]?.id,
      usage: isDouble ? undefined : usage,
      usageHt: !isDouble ? undefined : usageHt,
      usageNt: !isDouble ? undefined : usageNt,
      prices_data: selectedTariff?.prices_data || [],
      custom_price_data: selectedTariff?.custom_price_data || null,
      customer_type: formData.value.calculator.customer_type,
      customer_data: {
        salutation: formData.value.address.salutation,
        firstName: formData.value.address.firstName,
        lastName: formData.value.address.lastName,
        name1: formData.value.address.namePersonOne,
        name2: formData.value.address.namePersonTwo,
        title: formData.value.address.title,
        sharedLastName: formData.value.address.sharedLastName,
        coAddon: formData.value.address.coAddon,
        birthday: formData.value.address.birthday,
        company: formData.value.address.companyName,
        legalForm: formData.value.address.legalForm,
        owner: formData.value.address.legalForm ? {
          salutation: formData.value.address.companyOwnerSalutation,
          title: formData.value.address.companyOwnerTitle,
          firstName: formData.value.address.companyOwnerFirstName,
          lastName: formData.value.address.companyOwnerLastName,
          birthday: formData.value.address.companyOwnerBirthday,
          address: {
            street: formData.value.address.companyOwnerAddressStreet,
            houseNumber: formData.value.address.companyOwnerAddressHouseNumber,
            houseNumberAddon: formData.value.address.companyOwnerAddressHouseNumberAddon,
            postcode: formData.value.address.companyOwnerAddressPostcode,
            city: formData.value.address.companyOwnerAddressCity,
          },
        } : undefined,
        contactPerson: formData.value.address.companyContact,
        registerCourt: formData.value.address.companyRegistrationCourt,
        registerNumber: formData.value.address.companyRegistrationNumber,
        taxNumber: formData.value.address.companyTaxId,
        email: formData.value.address.email,
        phoneNumbers,
        customerNumber: formData.value.customer.customerNumber,
        meterNumber: formData.value.customer.meterNumber,
      },
      shipping_data: {
        postcode: formData.value.calculator.postcode,
        city: formData.value.calculator.city,
        street,
        number,
        numberSuffix: formData.value.address.houseNumberAddon,
        // IOSUITE-7461 — sent only when the address step actually asked the question, so no
        // other order process carries the key (it creates no mandatory field there).
        ...(formData.value.address.newDeliveryAddress
          ? { newDeliveryAddress: formData.value.address.newDeliveryAddress }
          : {}),
      },
      different_billing_address: formData.value.address.billingAddressDifferent,
      billing_type: formData.value.address.billingType || null,
      billing_data: {
        postcode: formData.value.address.billingAddress.postcode,
        city: formData.value.address.billingAddress.city,
        street: formData.value.address.billingAddress.street,
        number: formData.value.address.billingAddress.houseNumber,
        numberSuffix: formData.value.address.billingAddress.houseNumberAddon,
        firstName: formData.value.address.billingAddress.firstName,
        lastName: formData.value.address.billingAddress.lastName,
        pobox: formData.value.address.billingAddress.pobox,
      },
      ...(
        referralMarketingIsActive.value
          ? { referral_data: formData.value.referralMarketing }
          : {}
      ),
      uuid: isEdit ? formDataUuid : undefined,
    };
  };

  const getFormConsumptionPointPostParams = (categoryKey: string): Record<string, any> => {
    const dataConsumptionPoint = getDataConsumptionPointByCategoryKey(categoryKey);
    const formDataConsumptionPoint = formData.value.cartItems[categoryKey]?.consumptionPoint as FormDataConsumptionPointInterface | undefined;
    const isSupplierChange = formData.value.cartItems[categoryKey]?.consumptionPoint?.orderType === 'supplier-change';
    const isMoving = formData.value.cartItems[categoryKey]?.consumptionPoint?.orderType === 'moving';
    const isProductChange = formData.value.cartItems[categoryKey]?.consumptionPoint?.orderType === 'product-change';
    const showSelfCancellationDate = formDataConsumptionPoint?.previousSupplierCanceled
      && isSupplierChange
      && dataConsumptionPoint?.orderTypesSettings.supplierChangeType.showSelfCancellationDate;
    const showMovingChangeDate = isMoving
      && dataConsumptionPoint?.orderTypesSettings.moveType.showMovingChangeDate;
    // Portal relocations only — keeps the key out of io.order/io.sales payloads.
    const showMoveOutDate = isMoving
      && meta.value?.mode === 'relocation'
      && featureStore.isEnabled('center.fullIntegration')
      && !!dataConsumptionPoint?.orderTypesSettings.moveType.queryMoveOutDate;
    const showReading = (
      isMoving
      && dataConsumptionPoint?.orderTypesSettings.moveType.showMeterUsage
    ) || (
      isProductChange
      && dataConsumptionPoint?.orderTypesSettings.productChangeType.showMeterUsage
    );
    const showReadingAt = isMoving
      && dataConsumptionPoint?.orderTypesSettings.moveType.showDayOfReadingActualUsage;
    const showDesiredDate = (
      isSupplierChange
      && dataConsumptionPoint?.orderTypesSettings.supplierChangeType.showDesiredDate
    ) || (
      isProductChange
      && dataConsumptionPoint?.orderTypesSettings.productChangeType.showDesiredDate
    );

    return {
      ...getFormAddressPostParams(categoryKey),
      order_type: formData.value.cartItems[categoryKey]?.consumptionPoint.orderType,
      order_data: {
        ...(isSupplierChange ? {
          supplier: JSON.parse(formDataConsumptionPoint?.previousSupplier || '{}')?.value || null,
          customerNumber: formDataConsumptionPoint?.customerNumber,
          hasSelfCancelled: !!formDataConsumptionPoint?.previousSupplierCanceled,
          ...(showSelfCancellationDate ? {
            selfCancellationDate: formDataConsumptionPoint?.previousSupplierCanceledAt,
          } : {}),
        } : {}),

        ...((isProductChange || isMoving) ? {
          customerNumber: formDataConsumptionPoint?.customerNumber,
        } : {}),

        ...(showMovingChangeDate ? {
          desiredDeliveryDate: formDataConsumptionPoint?.movingAt,
        } : {}),

        ...(showMoveOutDate ? {
          moveOutDate: formDataConsumptionPoint?.moveOutDate,
        } : {}),

        ...(showReadingAt ? {
          dayOfReadingActualUsage: formDataConsumptionPoint?.readingAt,
        } : {}),

        isNextPossibleChangeDate: formDataConsumptionPoint?.nextPossibleChangeDate ?? false,
        ...(showDesiredDate && !formDataConsumptionPoint?.nextPossibleChangeDate ? {
          desiredDeliveryDate: formDataConsumptionPoint?.desiredDate,
        } : {}),
        meterNumber: formDataConsumptionPoint?.meterNumber,
        consumptionPointId: formDataConsumptionPoint?.consumptionPointId,

        ...(showReading ? {
          ...(isMoving ? {
            actualMeterUsage: formDataConsumptionPoint?.meterReading,
            ...(showReadingAt ? {
              dayOfReadingActualUsage: formDataConsumptionPoint?.readingAt,
            } : {}),
          } : {}),
          ...(isProductChange ? {
            meterReading: formDataConsumptionPoint?.meterReading,
          } : {}),
        } : {}),
      },

      uuid: formData.value.uuid,

      previous_address_data: formData.value.previousAddress,

      // Fiber Anschlussdaten cascade — validated on this step for internet products.
      ...(formData.value.cartItems[categoryKey]?.connectionData
        ? { connectionData: formData.value.cartItems[categoryKey].connectionData }
        : {}),
    };
  };

  const getFormPaymentPostParams = (categoryKey: string): Record<string, any> => {
    const isSepa = formData.value.payment.paymentType === 'sepa';
    const currentBrandSettings = brandsSettings.value?.[calculatorData.value.brand_id];
    const showBic = currentBrandSettings?.bicPayment || false;
    const showSepaText = currentBrandSettings?.isSepaTextActive || false;
    const yearlyPaymentActive = currentBrandSettings?.annualPayerActive || false;

    const cartItem = formData.value.cartItems[categoryKey];
    const paymentData = cartItem?.paymentData;
    const coupon = paymentData?.coupon;

    return {
      ...getFormConsumptionPointPostParams(categoryKey),
      payment_data: {
        type: formData.value.payment.paymentType,

        ...(isSepa ? {
          iban: formData.value.payment.iban,
          ...(showBic ? {
            bic: formData.value.payment.bic,
          } : {}),
          accountHolder: formData.value.payment.accountOwner,
          ...(showSepaText ? {
            isAcceptAgreements: formData.value.payment.sepa,
          } : {}),
        } : {}),

        ...(yearlyPaymentActive ? {
          yearlyPayment: formData.value.payment.yearly,
        } : {}),

        uuid: formData.value.uuid,

        voucherCodes: coupon,
      },
    };
  };

  const getFormCustomFieldsPostParams = (categoryKey: string, productId: number | string): Record<string, any> => ({
    ...getFormPaymentPostParams(categoryKey),
    ...(formData.value.customFields[productId] ? { custom_field_data: formData.value.customFields[productId] } : {}),
  });

  const getFormOverviewPostParams = (categoryKey: string, productId: number | string): Record<string, any> => {
    const categoryData = formData.value.cartItems[categoryKey]?.declarationOfIntent as FormDataCartItemDeclarationOfIntentInterface | undefined;
    const declarationOfIntent = formData.value.declarationOfIntent as FormDataDeclarationOfIntent | undefined;
    const marginalPriceData = selectedTariffs.value[categoryKey].marginal_price_data as Record<string, any> | undefined;
    const transitionProductId = selectedTariffs.value[categoryKey].transition_product_id;
    const cartItem = formData.value.cartItems[categoryKey];
    return {
      ...getFormCustomFieldsPostParams(categoryKey, productId),
      privacy_data: {
        isGeneralTermsAccept: categoryData?.generalTerms,
        isCustomerContractAccept: categoryData?.customerContract,
        isCustomerDisclaimerAccept: categoryData?.customerDisclaimer,
        // Fiber (internet) Vertragszusammenfassung intent — per-category, internet-only.
        isContractSummaryAccept: categoryData?.contractSummary,
        isPrivacyPolicyAccept: declarationOfIntent?.privacyPolicy,
        isCollectionOfInformationAccept: declarationOfIntent?.collectionOfInformation,
        isMarketingConsentCheck: declarationOfIntent?.marketingConsent,
        isSkipCreditCheck: formData.value.declarationOfIntent.isSkipCreditCheck,
        isSmartMeterAccept: formData.value.declarationOfIntent.smartMeter,
      },
      ...(hasMarginalPrice(marginalPriceData) ? {
        marginal_price_data: marginalPriceData,
      } : {}),
      uuid: formData.value.uuid,
      requestId: formData.value.requestId,
      external_order_id: formData.value.externalOrderId,
      transition_product_id: transitionProductId,
      transition_price_data: transitionTariffs.value.find(
        (tariff) => transitionProductId === tariff.id,
      )?.prices_data,
      // Fiber payload (camelCase; io.order maps to snake_case) — spread only when present, no commodity effect.
      ...(cartItem?.options?.length ? { options: cartItem.options } : {}),
      ...(cartItem?.connectionData ? { connectionData: cartItem.connectionData } : {}),
      ...(cartItem?.numberTransferData ? { numberTransferData: cartItem.numberTransferData } : {}),
    };
  };

  // Write Fiber config from calculator modal onSave into cart item (Ahrensburg getInternetConfigModalFormDefaults / mapNumberTransferData shape).
  const setFiberConfig = (
    categoryKey: string,
    form: FiberConfigFormInterface,
    options: ProductOptionInterface[],
  ): void => {
    // Create cart item lazily if missing so Fiber config is not dropped before consumption.
    let cartItem = formData.value.cartItems[categoryKey];
    if (!cartItem) {
      formData.value.cartItems[categoryKey] = {
        consumptionPoint: {} as FormDataConsumptionPointInterface,
        declarationOfIntent: {},
        paymentData: { coupon: '' },
      };
      cartItem = formData.value.cartItems[categoryKey];
    }

    cartItem.options = mapOptionsToPayload(form.options, options);
    cartItem.numberTransferData = {
      isNumberTransfer: form.isNumberTransfer,
      ...(form.isNumberTransfer ? {
        currentProvider: form.currentProvider,
        phoneNumber: form.phoneNumber,
        allowCancellation: form.allowCancellation,
        notIsConnectionOwner: form.notIsConnectionOwner,
        ...(form.notIsConnectionOwner
          ? { connectionOwner: { ...form.connectionOwner } as Record<string, string | undefined> }
          : {}),
      } : {}),
    };

    formData.value = { ...formData.value } as OrdersCreateFormDataInterface;
  };

  type CustomFieldsByProductType = Record<string, Record<string, any>>;

  const setProductCustomFields = (productId: number | string, fields: Record<string, any>, completeOverride = false): void => {
    const key = `${productId}`;
    const existing = (formData.value.customFields as unknown as CustomFieldsByProductType)[key] || {};
    (formData.value.customFields as unknown as CustomFieldsByProductType)[key] = {
      ...(completeOverride ? {} : existing),
      ...fields,
    };
    formData.value = { ...formData.value } as OrdersCreateFormDataInterface;
  };

  const removeProductCustomFieldKeys = (productId: number | string, keys: string[]): void => {
    const key = `${productId}`;
    const current = { ...((formData.value.customFields as unknown as CustomFieldsByProductType)[key] || {}) };
    keys.forEach((k) => { delete current[k]; });
    (formData.value.customFields as unknown as CustomFieldsByProductType)[key] = current;
    formData.value = { ...formData.value } as OrdersCreateFormDataInterface;
  };

  const getFormPostParams = (step: string, categoryKey: string, productId: number | string): Record<string, any> => {
    if (step === 'address') {
      return getFormAddressPostParams(categoryKey);
    }
    if (step === 'consumptionPoint') {
      return getFormConsumptionPointPostParams(categoryKey);
    }
    if (step === 'payment') {
      return getFormPaymentPostParams(categoryKey);
    }
    if (step === 'customFields') {
      return getFormCustomFieldsPostParams(categoryKey, productId);
    }
    if (step === 'overview') {
      return getFormOverviewPostParams(categoryKey, productId);
    }

    return {};
  };

  return {
    formData,
    continueAsLead,
    alreadyFetched,
    formErrors,
    selectedTariffs,
    getFormPostParams,
    setProductCustomFields,
    removeProductCustomFieldKeys,
    setFiberConfig,
  };
});

export default useOrdersCreateFormDataStore;
